import { Check, Clock, Info } from "lucide-react";
import { Stepper } from "@/components/Stepper";
import { mediaUrl } from "@/lib/api";
import { clp, minPrice, nightsList, OVERNIGHT, PRICE_MODE_LABEL } from "@/lib/format";

export function expAvailability(e, checkIn, checkOut, avail) {
  if (!checkIn) return { ok: false, label: "" };
  if (!OVERNIGHT.includes(e.slug)) {
    const s = avail[checkIn];
    if (!s) return { ok: true, label: "" };
    if (e.slug === "dia_compartido") return { ok: s.dia_compartido > 0, left: s.dia_compartido, label: s.dia_compartido > 0 ? `Quedan ${s.dia_compartido} cupos` : "Sin cupos" };
    return { ok: !!s[e.slug], label: s[e.slug] ? "Disponible" : "No disponible" };
  }
  if (!checkOut) return { ok: false, label: "" };
  const ss = nightsList(checkIn, checkOut).map((d) => avail[d]).filter(Boolean);
  if (e.slug === "cabana") {
    const ok = ss.every((s) => s.cabana);
    return { ok, label: ok ? "Disponible" : "No disponible" };
  }
  const left = ss.length ? Math.min(...ss.map((s) => s[e.slug])) : 0;
  return { ok: left > 0, left, label: left > 0 ? `Quedan ${left} espacio(s)` : "Sin espacios" };
}

function guestMax(e, a, extras) {
  if (e.slug === "cabana") {
    const add = extras.filter((x) => x.adds_guests > 0).reduce((s, x) => s + x.adds_guests, 0);
    return (e.base_guests || 5) + add;
  }
  if (e.slug === "dia_compartido" && a.left) return Math.min(e.max_people || 35, a.left);
  return e.max_people || 50;
}

const NOTES = {
  dia_privado: (e) => `Cobro mínimo equivalente a ${e.min_people} personas. Los huéspedes de la cabaña mantienen su derecho a usar piscina y quincho.`,
  cabana: (e) => `Capacidad estándar de ${e.base_guests} huéspedes. Para grupos más grandes se agregará la habitación adicional (sujeta a disponibilidad). Para más personas, consúltanos por WhatsApp.`,
  tinaja_dia: () => "Precio fijo por grupo: es el mismo para 2, 4 o 6 personas.",
};

export function StepExperience({ experiences, extras, mode, checkIn, checkOut, avail, exp, setExp, guests, setGuests, units, setUnits }) {
  const list = experiences.filter((e) => OVERNIGHT.includes(e.slug) === (mode === "noche"));
  return (
    <div className="grid gap-4" data-testid="step-experience">
      {list.map((e) => {
        const a = expAvailability(e, checkIn, checkOut, avail);
        const sel = exp === e.slug;
        const p = minPrice(e);
        return (
          <div key={e.slug} className={`rounded-3xl border transition-colors ${sel ? "border-[#5E6B52] bg-[#E9EFE6]/60" : "border-border bg-white"} ${!a.ok ? "opacity-55" : ""}`}>
            <button type="button" disabled={!a.ok} onClick={() => setExp(e.slug)} data-testid={`select-experience-${e.slug}`}
              className="w-full text-left flex gap-4 p-4 disabled:cursor-not-allowed">
              <img src={mediaUrl(e.image)} alt="" className="h-20 w-20 sm:h-24 sm:w-28 rounded-2xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-serif text-xl leading-tight">{e.name}</h4>
                  <span className={`h-6 w-6 shrink-0 rounded-full border grid place-items-center ${sel ? "bg-[#5E6B52] border-[#5E6B52] text-white" : "border-border"}`}>
                    {sel && <Check className="h-4 w-4" />}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />
                  {OVERNIGHT.includes(e.slug) ? `Check-in ${e.hora_inicio} · Check-out ${e.hora_fin}` : `${e.hora_inicio} a ${e.hora_fin} hrs`}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="font-semibold">{p > 0 ? `Desde ${clp(p)} ${PRICE_MODE_LABEL[e.price_mode]}` : "Precio por confirmar"}</span>
                  <span className={`text-xs font-semibold ${a.ok ? "text-[#3B7A57]" : "text-[#C84B31]"}`} data-testid={`availability-${e.slug}`}>{a.label}</span>
                </div>
              </div>
            </button>
            {sel && (
              <div className="px-4 pb-5 pt-1 space-y-4" data-testid="experience-options">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-medium">Personas</span>
                  <Stepper value={guests} onChange={setGuests} min={1} max={guestMax(e, a, extras)} testid="guests-stepper" />
                </div>
                {(e.slug === "carpas" || e.slug === "rodantes") && (
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm font-medium">{e.slug === "carpas" ? "Cantidad de carpas" : "Cantidad de casas rodantes"}</span>
                    <Stepper value={units} onChange={setUnits} min={1} max={Math.min(e.max_units || 5, a.left || 1)} testid="units-stepper" />
                  </div>
                )}
                {NOTES[e.slug] && <p className="text-xs text-muted-foreground flex gap-2"><Info className="h-4 w-4 shrink-0" />{NOTES[e.slug](e)}</p>}
                {e.includes?.length > 0 && <p className="text-xs text-muted-foreground"><b>Incluye:</b> {e.includes.join(" · ")}</p>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
